import { Animated, StyleSheet } from 'react-native'
import Reanimated from 'react-native-reanimated'
import { FiatActionButton } from './FiatActionButton'
import { FiatSubmissionIndicator } from './FiatSubmissionIndicator'

import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { ACTION_ENTERING, ACTION_EXITING } from '../utils/motion'
import { KYC_FORM_METRICS } from './kycFormTheme'

/**
 * What ends a form page, docked above whatever is up (KYC - R2, 2005:17707 /
 * 2017:18858 / 2017:19555).
 *
 * The design gives the slot one occupant at a time where it can: an age below
 * the floor puts the notice in the action's place, since no press clears it.
 * A blank field keeps its action — filling it in is what the press is for.
 * A long section scrolls under it, so it is pinned rather than left at the
 * end of the column.
 */
export function FiatFormAdvance({
  isSubmitting,
  validationError,
  isBlocked = false,
  shouldShowCta,
  bottom,
  onHeight,
  onPress,
}: {
  isLastStep: boolean
  isSubmitting: boolean
  /** What the page is refused for, when it has been tried. */
  validationError?: string
  /**
   * The refusal is not one a press can clear — an age below the floor, not a
   * field left blank. The notice then stands in the action's place.
   */
  isBlocked?: boolean
  /**
   * Whether the action is drawn. A typed page hides it until the page is
   * clean, since the keyboard's own return key is how it is tried; a page
   * with nothing to type into keeps it, because nothing else can try.
   */
  shouldShowCta: boolean
  /** Where it sits above the keyboard, the picker, or the home indicator. */
  bottom: Animated.WithAnimatedValue<number>
  /**
   * How tall it actually drew. The notice has a floor, not a ceiling — a
   * refusal the server worded, or a longer translation, wraps past it — so
   * the column cannot reserve its room from the metrics alone.
   */
  onHeight?: (height: number) => void
  onPress: () => void
}) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()

  return (
    <Animated.View
      pointerEvents="box-none"
      // Measured off the screen's own bottom edge: an absolute child does not
      // sit inside the padding whatever is up adds.
      style={[styles.dock, { bottom }]}
      onLayout={(event) => onHeight?.(event.nativeEvent.layout.height)}
    >
      {validationError === undefined ? null : (
        // Comes and goes on the action's own timing, since it stands where
        // the action would.
        <Reanimated.View entering={ACTION_ENTERING} exiting={ACTION_EXITING}>
          <Box
            accessibilityRole="alert"
            style={[
              styles.validationError,
              { backgroundColor: theme.form.errorNotice.fill },
            ]}
          >
            <Text
              variant="detail"
              style={[
                styles.validationErrorText,
                { color: theme.form.errorNotice.content },
              ]}
            >
              {validationError}
            </Text>
          </Box>
        </Reanimated.View>
      )}
      {isSubmitting ? <FiatSubmissionIndicator /> : null}
      {shouldShowCta && !isSubmitting && !isBlocked ? (
        <FiatActionButton label={t('common.continue')} onPress={onPress} />
      ) : null}
    </Animated.View>
  )
}

/**
 * How tall the dock stands before it has drawn, so the column has room to
 * reserve on the first paint. What it actually took comes back from
 * `onHeight`.
 */
export function advanceDockHeight(
  hasValidationError: boolean,
  isBlocked = false,
): number {
  if (isBlocked) {
    return KYC_FORM_METRICS.noticeHeight
  }
  return (
    KYC_FORM_METRICS.fieldHeight +
    (hasValidationError
      ? KYC_FORM_METRICS.noticeHeight + KYC_FORM_METRICS.rowGap
      : 0)
  )
}

const styles = StyleSheet.create({
  dock: {
    position: 'absolute',
    left: KYC_FORM_METRICS.screenInset,
    right: KYC_FORM_METRICS.screenInset,
    gap: KYC_FORM_METRICS.rowGap,
  },
  // The design's tinted notice (2052:5335): taller and rounder than a field
  // so it does not read as another refused one.
  validationError: {
    minHeight: KYC_FORM_METRICS.noticeHeight,
    borderRadius: KYC_FORM_METRICS.noticeRadius,
    paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
    paddingVertical: KYC_FORM_METRICS.rowGap,
    alignItems: 'center',
    justifyContent: 'center',
  },
  validationErrorText: {
    textAlign: 'center',
    letterSpacing: KYC_FORM_METRICS.noticeTracking,
  },
  spinner: {
    height: KYC_FORM_METRICS.fieldHeight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionRow: { alignItems: 'flex-end' },
  action: {
    width: KYC_FORM_METRICS.fieldHeight,
    height: KYC_FORM_METRICS.fieldHeight,
    borderRadius: KYC_FORM_METRICS.fieldHeight / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
})
